import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Plus, Send, Bell, Tag, Building2, Shield, Loader2, Users, RefreshCw, LogIn } from 'lucide-react';
import { useAppState } from '../../context/AppStateContext';
import { useAuth } from '../../context/AuthContext';
import { useResponsive } from '../../hooks/useResponsive';
import { NICHE_IMAGES } from '../../utils/constants';
import GroupDealsDrawer from '../groups/GroupDealsDrawer';
import GroupOverviewDrawer from '../groups/GroupOverviewDrawer';
import AddDealDrawer from '../groups/AddDealDrawer';

export default function GroupsTab({ onLoadingChange }) {
  const navigate = useNavigate();
  const {
    setSelectedGroup,
    groupsSubTab,
    setGroupsSubTab,
    joinGroup,
    joinedGroupIds,
    isolatedGroupDeals,
    setShowNotifications,
    unreadCount,
    showGroupDealsDrawer,
    setShowGroupDealsDrawer,
    showGroupOverviewDrawer,
    setShowGroupOverviewDrawer,
    // Shared API groups state
    apiGroups,
    loadingApiGroups,
    apiGroupsError,
    fetchApiGroups,
    profileForm,
  } = useAppState();
  const { isLoggedIn, isGuestMode, user } = useAuth();
  const { isDesktop } = useResponsive();

  // Get time-based greeting (matches HomeTab)
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };
  const userFirstName = user?.first_name || profileForm?.firstName || 'there';

  // Alias for compatibility with existing code
  const groups = apiGroups;
  const loadingGroups = loadingApiGroups;
  const groupsError = apiGroupsError;

  // Fetch groups from API on mount when user is logged in or in guest mode
  useEffect(() => {
    if (isLoggedIn || isGuestMode) {
      fetchApiGroups();
    }
  }, [isLoggedIn, isGuestMode, fetchApiGroups]);

  // Notify parent of loading state changes
  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(loadingGroups);
    }
  }, [loadingGroups, onLoadingChange]);

  // Refresh groups (force refresh)
  const handleRefresh = () => {
    if (isLoggedIn || isGuestMode) {
      fetchApiGroups(true); // force refresh
    }
  };

  // Helper to get group type from various possible field names
  const getGroupType = (g) => {
    const type = (g.access_level || g.type || g.group_type || g.visibility || '').toLowerCase();
    return type;
  };

  // Helper functions to check group types (matches Flutter)
  const isPublicGroup = (g) => {
    const type = getGroupType(g);
    // Check string types OR boolean is_public flag
    // Also treat empty/unknown types as public for discovery
    return type === 'public' || type === 'open' || type === '' || g.is_public === true;
  };

  const isPrivateGroup = (g) => {
    const type = getGroupType(g);
    return type === 'private' || type === 'closed' || g.is_private === true;
  };

  const isTeamGroup = (g) => {
    const type = getGroupType(g);
    return type === 'team' || type === 'organization' || type === 'org' || g.is_team === true;
  };

  // Filter groups by access_level/type from API response
  // For public tab: show ALL groups that are NOT explicitly private or team (inclusive for discovery)
  const publicGroups = useMemo(() => {
    return groups.filter(g => !isPrivateGroup(g) && !isTeamGroup(g));
  }, [groups]);

  const privateGroups = useMemo(() => {
    return groups.filter(g => isPrivateGroup(g) && !isTeamGroup(g));
  }, [groups]);

  const teamGroups = useMemo(() => {
    return groups.filter(g => isTeamGroup(g));
  }, [groups]);

  const displayGroups = groupsSubTab === 'public' ? publicGroups : groupsSubTab === 'private' ? privateGroups : teamGroups;

  // Log group structures for debugging
  useEffect(() => {
    console.log('=== GROUPS DEBUG ===');
    console.log('Total groups from API:', groups.length);
    console.log('Filtered counts:', {
      public: publicGroups.length,
      private: privateGroups.length,
      team: teamGroups.length,
    });
    if (groups.length > 0) {
      console.log('All groups with type info:', groups.map(g => ({
        name: g.name,
        uuid: g.uuid || g.id,
        access_level: g.access_level,
        type: g.type,
        group_type: g.group_type,
        visibility: g.visibility,
        is_public: g.is_public,
        is_private: g.is_private,
        is_team: g.is_team,
        resolvedType: getGroupType(g),
        isPublic: isPublicGroup(g),
        isPrivate: isPrivateGroup(g),
        isTeam: isTeamGroup(g),
      })));
    }
  }, [groups, publicGroups, privateGroups, teamGroups]);

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto scrollbar-hide">
        {/* Mobile Header - matches HomeTab */}
        {!isDesktop && (
          <header className="sticky top-0 bg-white z-10 px-4 pb-3 flex items-center justify-between border-b border-gray-100 pt-[calc(0.75rem+env(safe-area-inset-top))]">
            <div className="flex items-center gap-3">
              <img src="/logo/bonndleLogoIcon_Indigo.svg" alt="Bonndle" className="h-9 w-9" />
              <h1 className="font-bold text-gray-900 text-lg">
                {getGreeting()}, {userFirstName}
              </h1>
            </div>
            <button
              onClick={() => setShowNotifications(true)}
              className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-full"
            >
              <Bell size={22} />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-green-500 rounded-full" />
              )}
            </button>
          </header>
        )}
        <div className={`px-4 py-6 ${isDesktop ? 'max-w-4xl mx-auto' : ''}`}>
        {/* Toggle Switch - 3 tabs */}
        <div className="flex bg-gray-100 rounded-xl p-1 mb-6">
          <button
            onClick={() => setGroupsSubTab('public')}
            className={`flex-1 py-2.5 rounded-lg font-medium text-sm transition-colors ${
              groupsSubTab === 'public'
                ? 'bg-white text-indigo-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Public
          </button>
          <button
            onClick={() => setGroupsSubTab('private')}
            className={`flex-1 py-2.5 rounded-lg font-medium text-sm transition-colors ${
              groupsSubTab === 'private'
                ? 'bg-white text-indigo-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Private
          </button>
          <button
            onClick={() => setGroupsSubTab('teams')}
            className={`flex-1 py-2.5 rounded-lg font-medium text-sm transition-colors ${
              groupsSubTab === 'teams'
                ? 'bg-white text-indigo-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Teams
          </button>
        </div>

        {/* Content */}
        {/* Not logged in state - only show if not logged in AND not in guest mode */}
        {!isLoggedIn && !isGuestMode ? (
          <div className="text-center py-12">
            <LogIn size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 font-medium">Login to view groups</p>
            <p className="text-sm text-gray-400 mt-1">
              Sign in to see public, private, and team groups
            </p>
          </div>
        ) : (
          /* All Groups Content (Public/Private/Teams) */
          <>
            {/* Loading State */}
            {loadingGroups && (
              <div className="flex items-center justify-center py-12">
                <Loader2 size={32} className="animate-spin text-indigo-600" />
                <span className="ml-3 text-gray-500 font-medium">Loading groups...</span>
              </div>
            )}

            {/* Error State */}
            {!loadingGroups && groupsError && (
              <div className="text-center py-12">
                <Users size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 font-medium">{groupsError}</p>
                {groupsError === 'Groups feature is not available' ? (
                  <p className="text-sm text-gray-400 mt-2">
                    This feature is coming soon
                  </p>
                ) : (
                  <button
                    onClick={handleRefresh}
                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 transition-colors"
                  >
                    <RefreshCw size={16} />
                    Try Again
                  </button>
                )}
              </div>
            )}

            {/* Empty State */}
            {!loadingGroups && !groupsError && displayGroups.length === 0 && (
              <div className="text-center py-12">
                {groupsSubTab === 'teams' ? (
                  <Building2 size={48} className="mx-auto text-gray-300 mb-4" />
                ) : (
                  <Users size={48} className="mx-auto text-gray-300 mb-4" />
                )}
                <p className="text-gray-500 font-medium">
                  No {groupsSubTab} {groupsSubTab === 'teams' ? '' : 'groups '}yet
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  {groupsSubTab === 'public' && 'Public groups will appear here'}
                  {groupsSubTab === 'private' && 'Private groups you join will appear here'}
                  {groupsSubTab === 'teams' && 'Join an organization to see your teams here'}
                </p>
              </div>
            )}

            {/* Groups List */}
            {!loadingGroups && !groupsError && displayGroups.length > 0 && (
              <div className={`space-y-3 ${isDesktop ? 'grid grid-cols-2 gap-4 space-y-0' : ''}`}>
                {displayGroups.map((group) => {
                  const groupId = group.uuid || group.id;
                  const isJoined = joinedGroupIds.includes(groupId) || group.is_member === true;
                  // Use deals array from group details (fetched via getOne)
                  const dealsCount = group.deals?.length || group.merchants?.length || group.deals_count || (isolatedGroupDeals[groupId] || []).length;
                  // Use members array length from group details
                  const memberCount = group.members?.length || group.member_count || group.members_count || 0;
                  const groupImage = (group.name || '').toLowerCase().includes('bonndle')
                    ? '/logo/bonndleLogoIcon_Indigo.svg'
                    : group.image_url || group.avatar || group.image || NICHE_IMAGES[group.niche]?.[0] || NICHE_IMAGES.general[0];
                  const isTeam = group.access_level?.toLowerCase() === 'team';
                  const organization = group.organization || group.org_name || '';
                  const role = group.role || group.user_role || '';

                  return (
                    <button
                      key={groupId}
                      onClick={() => {
                        // Set selected group in state
                        setSelectedGroup(isTeam ? { ...group, isTeam: true } : group);

                        // Check if user is a member
                        if (isJoined) {
                          // Members go directly to chat
                          navigate(`/groups/${groupId}`);
                        } else {
                          // Non-members see group overview drawer first
                          setShowGroupOverviewDrawer(true);
                        }
                      }}
                      className="w-full flex items-center gap-4 p-4 bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow text-left"
                    >
                      <div className="w-16 h-16 bg-gray-100 rounded-full p-1 flex items-center justify-center flex-shrink-0">
                        <img
                          src={groupImage}
                          alt={group.name}
                          className="w-full h-full rounded-full object-cover"
                          onError={(e) => {
                            e.target.src = NICHE_IMAGES.general[0];
                          }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-gray-900 truncate">{group.name}</p>
                        <p className="text-sm text-gray-500">
                          {isTeam && organization ? `${organization} • ` : ''}
                          {memberCount} members{dealsCount > 0 ? ` • ${dealsCount} Active Deals` : ''}
                        </p>
                        {!isTeam && (group.created_at || group.createdAt || group._createdDate) && (
                          <p className="text-xs text-gray-400 truncate mt-0.5">
                            Created {new Date(group.created_at || group.createdAt || group._createdDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </p>
                        )}
                        {isTeam && role && (
                          <span className={`inline-flex items-center gap-1 mt-1 text-xs font-semibold ${
                            role.toLowerCase() === 'admin' ? 'text-amber-600' : 'text-indigo-600'
                          }`}>
                            {role.toLowerCase() === 'admin' && <Shield size={10} />}
                            {role}
                          </span>
                        )}
                        {!isTeam && isJoined && (
                          <span className="inline-flex items-center mt-1 text-xs text-emerald-600 font-semibold">
                            Joined
                          </span>
                        )}
                      </div>
                      <ChevronRight size={20} className="text-gray-400 flex-shrink-0" />
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}
        </div>
      </div>

      {/* Drawers */}
      <GroupDealsDrawer />
      <GroupOverviewDrawer />
      <AddDealDrawer />
    </div>
  );
}

function GroupChat({ group, deals, onBack, onOpenDeals }) {
  const { isDesktop } = useResponsive();
  const { getGroupMessages, addGroupMessage, profileForm, setShowNotifications, unreadCount } = useAppState();
  const [newMessage, setNewMessage] = useState('');

  const messages = getGroupMessages(group.id);

  const handleSendMessage = () => {
    if (!newMessage.trim()) return;

    addGroupMessage(group.id, {
      type: 'message',
      author: profileForm.firstName || 'You',
      avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${profileForm.firstName || 'You'}`,
      text: newMessage,
    });
    setNewMessage('');
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto scrollbar-hide">
        {/* Mobile Header - matches HomeTab */}
        {!isDesktop && (
          <header className="sticky top-0 bg-white z-10 px-4 pb-3 flex items-center justify-between border-b border-gray-100 pt-[calc(0.75rem+env(safe-area-inset-top))]">
            <div className="flex items-center gap-3">
              <img src="/logo/bonndleLogoIcon_Indigo.svg" alt="Bonndle" className="h-9 w-9" />
              <h1 className="font-bold text-gray-900 text-lg">
                {getGreeting()}, {userFirstName}
              </h1>
            </div>
            <button
              onClick={() => setShowNotifications(true)}
              className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-full"
            >
              <Bell size={22} />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-green-500 rounded-full" />
              )}
            </button>
          </header>
        )}
        <div className={`px-4 py-4 ${isDesktop ? 'max-w-4xl mx-auto' : 'pb-24'}`}>
          {/* Group Chat Header - below app header */}
          <div className="flex items-center gap-3 p-3 bg-white rounded-2xl shadow-sm mb-4">
            <button onClick={onBack} className="p-2 text-gray-600 hover:bg-gray-100 rounded-full active:scale-90 transition-transform">
              <ArrowLeft size={20} />
            </button>
            <div className="w-11 h-11 bg-gray-100 rounded-full p-0.5 flex items-center justify-center flex-shrink-0">
              <img
                src={(group.name || '').toLowerCase().includes('bonndle')
                  ? '/logo/bonndleLogoIcon_Indigo.svg'
                  : group.image_url || group.avatar || group.image || NICHE_IMAGES[group.niche]?.[0] || NICHE_IMAGES.general[0]}
                alt={group.name}
                className="w-full h-full rounded-full object-cover"
              />
            </div>
            <div className="flex-1">
              <p className="font-bold text-gray-900 truncate">{group.name}</p>
              <p className="text-sm text-gray-500">{group.members} members • {deals.length} deals</p>
            </div>
          </div>

          {/* Interactive Active Deals Banner */}
          {deals.length > 0 && (
            <div
              onClick={onOpenDeals}
              className="w-full flex items-center gap-4 p-4 bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow text-left mb-4 cursor-pointer"
            >
              {/* Stacked Logo Avatars */}
              <div className="flex -space-x-3">
                {deals.slice(0, 3).map((deal, index) => (
                  <div
                    key={deal.id}
                    className={`w-10 h-10 rounded-full border-2 border-white ${deal.color || 'bg-indigo-600'} flex items-center justify-center text-white text-xs font-black shadow-sm`}
                    style={{ zIndex: 3 - index }}
                  >
                    {deal.logo}
                  </div>
                ))}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-gray-900">Active Deals</p>
                <p className="text-sm text-gray-500">{deals.length} offer{deals.length !== 1 ? 's' : ''} available</p>
              </div>
              <button className="px-3 py-2 bg-indigo-600 text-white text-xs font-bold rounded-lg whitespace-nowrap">
                Get Cashback
              </button>
            </div>
          )}

          {/* Chat Messages Area */}
          <div className="space-y-3">
            {messages.length === 0 ? (
              /* Empty State */
              <div className="w-full p-4 bg-white rounded-2xl shadow-sm text-center">
                <p className="text-sm text-gray-500">Start coordinating with your group!</p>
                <p className="text-xs text-gray-400 mt-1">Messages will appear here</p>
              </div>
            ) : (
              /* Messages List */
              messages.map((msg) => (
                <div key={msg.id} className="flex gap-3">
                  <img
                    src={msg.avatar}
                    alt={msg.author}
                    className="w-8 h-8 rounded-full bg-gray-100 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-sm text-gray-900">{msg.author}</span>
                      {msg.type === 'deal_comment' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-600 text-[10px] font-bold rounded-full">
                          <Tag size={10} />
                          {msg.dealBrand}
                        </span>
                      )}
                      <span className="text-xs text-gray-400">{msg.time}</span>
                    </div>
                    <div className="bg-white rounded-2xl rounded-tl-sm p-3 shadow-sm">
                      <p className="text-sm text-gray-700">{msg.text}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Input - fixed on mobile, flexbox on desktop */}
      {!isDesktop ? (
        <div className="fixed bottom-[calc(5rem+env(safe-area-inset-bottom))] left-0 right-0 bg-white border-t border-gray-100 z-20 px-4 py-3">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
              placeholder="Message group..."
              className="flex-1 px-4 py-3 bg-gray-100 rounded-xl text-sm font-medium text-gray-900 placeholder-gray-400 outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <button
              onClick={handleSendMessage}
              disabled={!newMessage.trim()}
              className="p-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send size={20} />
            </button>
          </div>
        </div>
      ) : (
        <div className="shrink-0 bg-white border-t border-gray-100">
          <div className="px-4 py-3 max-w-4xl mx-auto">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                placeholder="Message group..."
                className="flex-1 px-4 py-3 bg-gray-100 rounded-xl text-sm font-medium text-gray-900 placeholder-gray-400 outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <button
                onClick={handleSendMessage}
                disabled={!newMessage.trim()}
                className="p-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send size={20} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
